import type { CollectionViewParamsConfig } from 'framework/ViewTypes';
import { type FilterState } from 'framework/ViewTypes/components';
import { FilterCondition } from 'framework/types';
import { useCollectionPager } from 'framework/ViewTypes/hooks';
import { CANDIDATE_TAGS } from 'domain/shared/newVacancy';
import type { SourcingCandidateRow, SourcingFilterOptions } from '../types';
import useRecruitmentPoolSourcingService from '../useRecruitmentPoolSourcingService';
import mapCandidateToRow from '../utils/mapCandidateToRow';
import { SOURCING_FILTER_KEYS, SOURCING_FILTERS, SUPPORTED_COMPETENCY_LEVELS } from './filters/constants';
import {
  findSourcingSpecialization,
  isSourcingSelectionComplete,
  mapCollectionFiltersToSourcingFilters,
  mapSourcingFiltersToSavedParameters,
} from './filters/utils/sourcingFilterModel';

type UseRecruitmentPoolSourcingCollectionParams = {
  recruitmentPoolId?: string;
  filterOptions?: SourcingFilterOptions;
};

const VIEW_PARAMS_CONFIG: CollectionViewParamsConfig<FilterState> = {
  defaultFilters: {},
  filterRules: {
    [SOURCING_FILTERS.stricts]: { condition: FilterCondition.IN, values: SOURCING_FILTER_KEYS },
    [SOURCING_FILTERS.specialization]: { condition: FilterCondition.EQ },
    [SOURCING_FILTERS.technology]: { condition: FilterCondition.EQ },
    [SOURCING_FILTERS.levels]: { condition: FilterCondition.IN, values: SUPPORTED_COMPETENCY_LEVELS },
    [SOURCING_FILTERS.skills]: { condition: FilterCondition.IN },
    [SOURCING_FILTERS.englishLevel]: { condition: FilterCondition.EQ },
    [SOURCING_FILTERS.location]: { condition: FilterCondition.IN },
    [SOURCING_FILTERS.tags]: { condition: FilterCondition.IN, values: Object.keys(CANDIDATE_TAGS) },
    [`${SOURCING_FILTERS.lastInteraction}.from`]: { condition: FilterCondition.EQ },
    [`${SOURCING_FILTERS.lastInteraction}.to`]: { condition: FilterCondition.EQ },
  },
  sortableColumns: [],
  cache: false,
};

const useRecruitmentPoolSourcingCollection = ({
  recruitmentPoolId,
  filterOptions,
}: UseRecruitmentPoolSourcingCollectionParams) => {
  const { search } = useRecruitmentPoolSourcingService();

  const collection = useCollectionPager<SourcingCandidateRow, FilterState>({
    pageSize: 30,
    viewParamsConfig: VIEW_PARAMS_CONFIG,
    collectionKey: `${recruitmentPoolId}:${filterOptions?.id ?? 'options-loading'}`,
    enabled: Boolean(recruitmentPoolId),
    fetchPage: async ({ pageNumber, pageSize, filters }) => {
      const sourcingFilters = mapCollectionFiltersToSourcingFilters(filters);
      const specialization = filterOptions
        ? findSourcingSpecialization(filterOptions, sourcingFilters.specializationId)
        : undefined;
      const technology = specialization?.technologies.find(
        option => option.technologyId === sourcingFilters.technologyId,
      );
      const parameters = mapSourcingFiltersToSavedParameters(sourcingFilters);

      if (
        !recruitmentPoolId ||
        !filterOptions ||
        !specialization ||
        !parameters ||
        !isSourcingSelectionComplete(filterOptions, sourcingFilters)
      ) {
        return { result: [], pageNumber, totalPages: 0, totalElements: 0 };
      }

      const response = await search(recruitmentPoolId, {
        pageNumber,
        pageSize,
        ...parameters,
      });
      const candidates = response.result ?? [];
      return {
        result: candidates.map(candidate => mapCandidateToRow(candidate, specialization, technology)),
        pageNumber: response.pageNumber ?? pageNumber,
        totalPages: response.totalPages ?? 0,
        totalElements: response.totalElements ?? candidates.length,
      };
    },
  });

  return { ...collection };
};

export default useRecruitmentPoolSourcingCollection;
